import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import type { AppointmentsDetailsResponse } from '@/types/scheduling-api';

/**
 * POST /api/Appointment/Recurring — list every appointment in a
 * recurring series. Used by the Appointments tab inside
 * ViewAppointmentPanel to show the series timeline.
 *
 * Flutter parity: `recurringAppointmentsList()` →
 * `scheduling_data_source_impl.dart:1054-1079` (note: different from
 * the generic `/api/Appointment` list endpoint).
 *
 * Wire-shape (dev curl verified 2026-05-13):
 *   {
 *     LoggedInUserTimezone: "",
 *     filter: "Day",
 *     organizationId, practiceId,
 *     status: [], location: [],
 *     pageNumber: 1, pageSize: 10,
 *     recurringId: "<series guid>"
 *   }
 *
 * Returns the standard AppointmentsDetailsResponse shape — same as
 * the day-list endpoint — so consumers can reuse the existing
 * mapAppointmentsToDayList() mapper without translation.
 */

interface UseRecurringAppointmentListParams {
  recurringId: string | null | undefined;
  pageNumber?: number;
  pageSize?: number;
  /** Status code filter — empty array = no filter. */
  status?: Array<{ code: string; display?: string; definition?: string }>;
  /** Location id filter — empty array = no filter. */
  location?: string[];
}

export function useRecurringAppointmentList(
  params: UseRecurringAppointmentListParams,
) {
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';

  return useQuery({
    queryKey: [
      'recurring-appointment-list',
      params.recurringId,
      params.pageNumber ?? 1,
      params.pageSize ?? 10,
      params.status ?? [],
      params.location ?? [],
      orgId,
      practiceId,
    ],
    enabled: !!params.recurringId && !!orgId && !!practiceId,
    queryFn: ({ signal }) =>
      post<AppointmentsDetailsResponse>(
        '/api/Appointment/Recurring',
        {
          LoggedInUserTimezone: '',
          filter: 'Day',
          organizationId: orgId,
          practiceId: practiceId,
          status: params.status ?? [],
          location: params.location ?? [],
          pageNumber: params.pageNumber ?? 1,
          pageSize: params.pageSize ?? 10,
          recurringId: params.recurringId,
        },
        { signal },
      ),
  });
}
